import React, { useContext, useEffect, useState } from 'react';
import {
    Button,
    ConfigProvider,
    Modal,
    Alert,
    Table,
    Tag,
    Space,
    Typography,
    Divider,
    message,
    Popconfirm,
    Tabs
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import locale from "antd/es/locale/es_ES";
import { AuthContext } from "../../../../config/AuthContext";
import { GetJBbddProblemesIdEnviament } from "../../../../dao/bbdd/GetJBbddProblemesIdEnviament";
import { GetBbddProblemaIdUsuariId } from "../../../../dao/bbdd/GetBbddProblemaIdUsuariId";
import { GetJBbddProblemesId } from "../../../../dao/bbdd/GetJBbddProblemesId";
import { DeleteBbddEnviament } from "../../../../dao/bbdd/DeleteBbddEnviament";
import ReactMarkdown from "react-markdown";
import Editor from "@monaco-editor/react";
import ErDiagramIde from "../../disseny/ErDiagramIde";
import ScriptRelationalIde from "../../disseny/ScriptRelationalIde";
import RelationalDiagramIde from "../../disseny/RelationalDiagramIde";
import { validateErDiagram, ValidationResult } from "../../disseny/engine/ErValidator";
import { validateScriptRelational, parseScriptTextToData, ScriptValidationResult } from "../../disseny/engine/ScriptRelationalEngine";
import { validateRelationalDiagram, RelationalValidationResult } from "../../disseny/engine/RelationalValidator";
import { getPenalitzacioPerIntent, aplicarPenalitzacio } from "../../disseny/engine/notaPenalitzacio";
import {
    SendOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    HistoryOutlined,
    DeleteOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;

// Color gradual segons la nota (0-100): 100 verd, 50-99 taronja->groc-verd, <50 vermell fosc->clar.
const lerpColorRGB = (a: [number, number, number], b: [number, number, number], t: number): string => {
    const r = Math.round(a[0] + (b[0] - a[0]) * t);
    const g = Math.round(a[1] + (b[1] - a[1]) * t);
    const bl = Math.round(a[2] + (b[2] - a[2]) * t);
    return `rgb(${r}, ${g}, ${bl})`;
};
const notaColor = (valRaw: number): string => {
    const val = Math.max(0, Math.min(100, Number(valRaw) || 0));
    if (val >= 100) return '#389e0d';
    if (val >= 50) {
        const t = (val - 50) / 49;
        return lerpColorRGB([212, 107, 8], [160, 217, 25], t);
    }
    const t = val / 49;
    return lerpColorRGB([92, 0, 17], [255, 77, 79], t);
};

interface Props {
    element: any;
    visible: boolean;
    onClose?: (value: boolean) => void;
    editar?: boolean;
    id_alumne?: string | number;
    id_practica?: string | number;
    inline?: boolean; // Quan es renderitza incrustat dins d'un panell o pestanya sense embolcall de Modal
}

const RevisioModal: React.FC<Props> = (props: Props) => {
    const { isLoggedIn, user, userId } = useContext(AuthContext);
    const isTeacher = user?.tipus === "0" || user?.tipus === "1";

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [enviaments, setEnviaments] = useState<any[]>([]);
    const [selectedEnvId, setSelectedEnvId] = useState<number | null>(null);

    // Dades de l'enviament seleccionat
    const [enunciat, setEnunciat] = useState("");
    const [actual, setActual] = useState("");
    const [errors, setErrors] = useState("");
    const [dades, setDades] = useState<any[]>([]);
    const [dadesA, setDadesA] = useState<any[]>([]);
    const [designEvalResult, setDesignEvalResult] = useState<any | null>(null);

    const [ultimGuardat, setUltimGuardat] = useState<any | null>(null);

    // Solució de referència (correccio) i rúbrica (checks/test_config) resoltes de forma robusta.
    // Es guarden en estat perquè, si l'element que rep el modal no les porta, les recuperem del backend.
    const [solucioRef, setSolucioRef] = useState<any | null>(null);
    const [customConfRef, setCustomConfRef] = useState<any | null>(null);

    const targetUserId = props.id_alumne ? String(props.id_alumne) : String(userId);

    // Converteix un valor de "correccio" (objecte, JSON string o string amb prefix) en objecte.
    const parseSolucio = (correccio: any): any | null => {
        if (!correccio) return null;
        if (typeof correccio === 'object') return correccio;
        if (typeof correccio === 'string') {
            const s = correccio.trim();
            if (!s) return null;
            // Si és JSON (o conté un JSON darrere d'un prefix com "ER#{...}"), el parsegem.
            const braceIdx = s.indexOf('{');
            if (s.startsWith('{') || braceIdx !== -1) {
                const jsonCandidate = s.startsWith('{') ? s : s.slice(braceIdx);
                try { return JSON.parse(jsonCandidate); } catch (_) { /* continua */ }
            }
            // No és JSON: pot ser una solució Script en TEXT PLA. La retornem tal qual
            // perquè l'engine de Script la parsegi (parseScriptTextToData).
            return s;
        }
        return null;
    };

    // Converteix el camp "checks"/"test_config" en configuració de rúbrica.
    const parseRubrica = (element: any): any | null => {
        if (!element) return null;
        if (element.test_config) {
            try { return typeof element.test_config === 'string' ? JSON.parse(element.test_config) : element.test_config; } catch (_) {}
        }
        if (element.checks) {
            try {
                let jsonPart = element.checks;
                const hashIdx = element.checks.indexOf('#');
                if (hashIdx !== -1 && element.checks.slice(hashIdx + 1).trim().startsWith('{')) {
                    jsonPart = element.checks.slice(hashIdx + 1).trim();
                }
                return JSON.parse(jsonPart);
            } catch (_) {}
        }
        return null;
    };

    useEffect(() => {
        if (isLoggedIn && props.visible) {
            checkDades();
        }
    }, [props.element, props.id_alumne, props.visible]);

    async function checkDades() {
        setLoading(true);
        try {
            const probId = String(props.element?.id || props.element?.id_problema || '');
            const effectivePractica = props.id_practica !== undefined ? String(props.id_practica) : (props.element?.id_practica !== undefined ? String(props.element.id_practica) : undefined);
            const act: any = await GetJBbddProblemesIdEnviament(probId, targetUserId, effectivePractica);
            if (Array.isArray(act) && act.length > 0) {
                // Assegurar-nos que cada element tingui clau única per a la taula Ant Design
                act.forEach((item: any, idx: number) => {
                    if (!item.key) item.key = item.id || idx;
                });
                // Obtenir configuració de solució i rúbrica de forma robusta.
                let solObj: any = parseSolucio(props.element?.correccio);
                let customConf: any = parseRubrica(props.element);

                // Fallback: si l'element no porta la solució (p. ex. s'ha passat un objecte
                // d'enviament en lloc del problema complet), recuperem el problema del backend.
                const tipusDisseny = props.element?.tipus === 'ER' || props.element?.tipus === 'Script' || props.element?.tipus === 'Relacional';
                if (!solObj && tipusDisseny) {
                    try {
                        const probFull: any = await GetJBbddProblemesId(probId);
                        if (probFull) {
                            solObj = parseSolucio(probFull.correccio);
                            if (!customConf) customConf = parseRubrica(probFull);
                        }
                    } catch (_) {}
                }
                setSolucioRef(solObj);
                setCustomConfRef(customConf);

                // Enriquir cada enviament amb la seva nota calculada al client.
                // Per a exercicis de DISSENY (ER/Script/Relacional) SEMPRE recalculem la nota
                // amb el validador, encara que a la BD n'hi hagi una desada: la desada pot ser
                // incorrecta (p. ex. 0 per bugs antics d'avaluació) i la font de veritat del
                // detall és el validador del client. Així la taula i el detall són consistents.
                act.forEach((item: any) => {
                    const isDraftItem = item.draft || item.errors === 'DRAFT_GUARDAT';
                    if (isDraftItem) return;

                    const esDisseny = props.element?.tipus === 'ER' || props.element?.tipus === 'Script' || props.element?.tipus === 'Relacional';
                    const textContent = item.actual || "";
                    const potRecalcular = esDisseny && solObj && textContent;
                    const noTeNota = item.nota === null || item.nota === undefined;

                    if (potRecalcular || noTeNota) {
                        if (solObj && textContent) {
                            try {
                                if (props.element?.tipus === 'ER' || textContent.includes('"type":"ER"') || textContent.includes('"type": "ER"')) {
                                    const stuObj = typeof textContent === 'string' ? JSON.parse(textContent) : textContent;
                                    const evalRes = validateErDiagram(stuObj, solObj, customConf, 0);
                                    item.nota = evalRes.score;
                                } else if (props.element?.tipus === 'Script' || textContent.includes('"type":"Script"') || textContent.includes('"type": "Script"')) {
                                    const stuObj = typeof textContent === 'string' && textContent.trim().startsWith('{')
                                        ? JSON.parse(textContent)
                                        : parseScriptTextToData(textContent);
                                    const solParsed = (solObj.tables && Array.isArray(solObj.tables))
                                        ? solObj
                                        : (typeof solObj === 'string' ? parseScriptTextToData(solObj) : solObj);
                                    const evalRes = validateScriptRelational(stuObj, solParsed, customConf, 0);
                                    item.nota = evalRes.score;
                                } else if (props.element?.tipus === 'Relacional' || textContent.includes('"type":"Relacional"') || textContent.includes('"type": "Relacional"') || textContent.includes('"type":"RELACIONAL"')) {
                                    const stuObj = typeof textContent === 'string' ? JSON.parse(textContent) : textContent;
                                    const evalRes = validateRelationalDiagram(stuObj, solObj, customConf, 0);
                                    item.nota = evalRes.score;
                                }
                            } catch (_) {}
                        }
                    }
                });

                // Separar enviaments (avaluats) d'esborranys guardats (draft)
                const realEnviaments = act.filter((item: any) => !item.draft && item.errors !== 'DRAFT_GUARDAT');
                const lastSavedDraft = act.find((item: any) => item.draft || item.errors === 'DRAFT_GUARDAT');
                const lastRealSubmission = realEnviaments.length > 0 ? realEnviaments[0] : null;

                // El professor només veu l'últim guardat si és diferent a l'últim enviat (o si encara no ha enviat cap)
                let showDraftForTeacher = false;
                if (lastSavedDraft) {
                    if (!lastRealSubmission) {
                        showDraftForTeacher = true;
                    } else if (lastSavedDraft.id > lastRealSubmission.id && lastSavedDraft.actual !== lastRealSubmission.actual) {
                        showDraftForTeacher = true;
                    }
                }

                setUltimGuardat(showDraftForTeacher ? lastSavedDraft : null);

                // L'alumne només veu la llista d'enviaments reals.
                setEnviaments(realEnviaments);

                // Per defecte seleccionar: si és professor i hi ha esborrany guardat més recent, seleccionar l'esborrany; altrament el primer enviament.
                // Passem solObj/customConf resolts perquè l'estat encara no s'ha actualitzat en aquest mateix tick.
                if (isTeacher && showDraftForTeacher && lastSavedDraft) {
                    selectSubmission(lastSavedDraft, solObj, customConf);
                } else if (realEnviaments.length > 0) {
                    selectSubmission(realEnviaments[0], solObj, customConf);
                } else if (act.length > 0) {
                    selectSubmission(act[0], solObj, customConf);
                } else {
                    resetSelection();
                }
            } else {
                setEnviaments([]);
                setUltimGuardat(null);
                resetSelection();
            }
        } catch (e) {
            console.error("Error al carregar enviaments de BBDD:", e);
            setEnviaments([]);
            setUltimGuardat(null);
            resetSelection();
        } finally {
            setLoading(false);
        }
    }

    function resetSelection() {
        setSelectedEnvId(null);
        setActual("");
        setErrors("");
        setDades([]);
        setDadesA([]);
        setDesignEvalResult(null);
        setEnunciat(props.element?.enunciat || props.element?.descripcio || "");
    }

    function selectSubmission(item: any, solucioOverride?: any, customConfOverride?: any) {
        if (!item) return;
        setSelectedEnvId(item.id);
        setErrors(item.salida || item.errors || "");
        setActual(item.actual || "");
        setEnunciat(item.descripcio || props.element?.enunciat || props.element?.descripcio || "");

        // Taules comparatives SQL: només si json_correcte/json_alumne són ARRAYS de files.
        // (Per a disseny, json_alumne conté el feedback com a objecte, no files SQL.)
        try {
            const parsed = item.jsonCorrecte ? JSON.parse(item.jsonCorrecte) : [];
            setDades(Array.isArray(parsed) ? parsed : []);
        } catch {
            setDades([]);
        }

        try {
            const parsed = item.jsonAlumne ? JSON.parse(item.jsonAlumne) : [];
            setDadesA(Array.isArray(parsed) ? parsed : []);
        } catch {
            setDadesA([]);
        }

        // PRIORITAT: si l'enviament ja porta el detall d'avaluació desat a la BD (json_alumne,
        // amb el feedback que ha calculat el backend en avaluar), el fem servir directament i
        // NO recalculem res al client. El frontend només renderitza.
        try {
            if (item.jsonAlumne && typeof item.jsonAlumne === 'string' && item.jsonAlumne.trim().startsWith('{')) {
                const saved = JSON.parse(item.jsonAlumne);
                if (saved && Array.isArray(saved.feedback)) {
                    setDesignEvalResult({
                        score: saved.notaBase !== undefined ? saved.notaBase : 0,
                        notaBase: saved.notaBase,
                        feedback: saved.feedback,
                        summary: saved.summary || undefined,
                        isCorrect: (saved.notaBase || 0) >= 100
                    });
                    return;
                }
            }
        } catch (_) { /* si falla, recalculem a sota */ }

        // Si és exercici de disseny (ER, Script, Relacional), avaluar la resposta d'aquest enviament respecte la solució.
        // Fem servir la solució ja resolta (override o estat), amb fallback a l'element.
        try {
            const solObj: any = solucioOverride !== undefined ? solucioOverride : (solucioRef || parseSolucio(props.element?.correccio));
            const customConf: any = customConfOverride !== undefined ? customConfOverride : (customConfRef || parseRubrica(props.element));
            const textContent = item.actual || "";

            if (solObj && textContent) {
                if (isEr || textContent.includes('"type":"ER"') || textContent.includes('"type": "ER"')) {
                    const stuObj = typeof textContent === 'string' ? JSON.parse(textContent) : textContent;
                    const res = validateErDiagram(stuObj, solObj, customConf, 0);
                    setDesignEvalResult(res);
                } else if (isScript || textContent.includes('"type":"Script"') || textContent.includes('"type": "Script"')) {
                    const stuObj = typeof textContent === 'string' && textContent.trim().startsWith('{')
                        ? JSON.parse(textContent)
                        : parseScriptTextToData(textContent);
                    const solParsed = (solObj.tables && Array.isArray(solObj.tables))
                        ? solObj
                        : (typeof solObj === 'string' ? parseScriptTextToData(solObj) : solObj);
                    const res = validateScriptRelational(stuObj, solParsed, customConf, 0);
                    setDesignEvalResult(res);
                } else if (isRelational || textContent.includes('"type":"Relacional"') || textContent.includes('"type": "Relacional"') || textContent.includes('"type":"RELACIONAL"')) {
                    const stuObj = typeof textContent === 'string' ? JSON.parse(textContent) : textContent;
                    const res = validateRelationalDiagram(stuObj, solObj, customConf, 0);
                    setDesignEvalResult(res);
                } else {
                    setDesignEvalResult(null);
                }
            } else {
                setDesignEvalResult(null);
            }
        } catch (e) {
            console.error("Error en avaluar la tramesa de disseny històrica:", e);
            setDesignEvalResult(null);
        }
    }

    // Acció de RE-ENVIAR per al professor
    async function handleReEnviar() {
        if (!isTeacher) return;
        if (!actual || actual.trim() === "") {
            message.warning("No hi ha contingut per re-enviar.");
            return;
        }

        setSubmitting(true);
        try {
            // Determinar l'ID del problema i de l'alumne
            const probId = String(props.element?.id || props.element?.id_problema);
            const userToSubmit = targetUserId;

            // Tipus d'exercici
            let tipus = props.element?.tipus;
            if (!tipus) {
                if (isEr) tipus = 'ER';
                else if (isScript) tipus = 'Script';
                else if (isRelational) tipus = 'Relacional';
                else if (isMongo) tipus = 'NoSQL';
                else tipus = 'SQL';
            }

            // Esquema de la base de dades
            let schema = props.element?.esquema || props.element?.schema || props.element?.nom || '';

            // Guardar com a nova entrega oficial de l'alumne (avaluada)
            const effectivePractica = props.id_practica !== undefined ? String(props.id_practica) : (props.element?.id_practica !== undefined ? String(props.element.id_practica) : undefined);
            const response = await GetBbddProblemaIdUsuariId(
                probId,
                userToSubmit,
                actual,
                tipus,
                schema,
                'avaluar',
                undefined,
                effectivePractica
            );

            if (response && response.salida && response.salida.startsWith('OK')) {
                message.success("Nou enviament registrat i avaluat correctament!");
            } else if (response && response.salida) {
                message.warning("Nou enviament registrat (amb errors d'execució: " + response.salida + ")");
            } else {
                message.success("Nou enviament registrat correctament!");
            }

            // Recarregar la llista d'enviaments
            await checkDades();
        } catch (err: any) {
            console.error("Error al re-enviar:", err);
            message.error("Error al registrar el re-enviament: " + (err?.message || err));
        } finally {
            setSubmitting(false);
        }
    }

    // Acció d'ELIMINAR ENVIAMENT per al professor
    async function handleDeleteEnviament(idResposta: number) {
        if (!isTeacher) return;
        setLoading(true);
        try {
            await DeleteBbddEnviament(idResposta);
            message.success(`Enviament #${idResposta} eliminat correctament.`);
            // Recarregar la llista d'enviaments (recalcularà la nota i la selecció)
            await checkDades();
        } catch (err: any) {
            console.error("Error eliminant l'enviament:", err);
            message.error("Error al eliminar l'enviament: " + (err?.message || err));
            setLoading(false);
        }
    }

    const handleCancel = () => {
        props.onClose?.(false);
    };

    // Tipus d'exercici
    const isEr = props.element?.tipus === 'ER' || actual?.includes('"type":"ER"') || actual?.includes('"type": "ER"');
    const isScript = props.element?.tipus === 'Script' || actual?.includes('"type":"Script"') || actual?.includes('"type": "Script"');
    const isRelational = props.element?.tipus === 'Relacional' || actual?.includes('"type":"Relacional"') || actual?.includes('"type": "Relacional"') || actual?.includes('"type":"RELACIONAL"') || actual?.includes('"type": "RELACIONAL"');
    const isMongo = props.element?.tipus === 'NoSQL' || props.element?.tipus === 'NOSQL' || actual?.includes('db.');

    // Columnes de la taula d'enviaments històrics
    const columns: ColumnsType<any> = [
        {
            title: '# ID',
            dataIndex: 'id',
            key: 'id',
            width: 75,
            render: (id, record, index) => {
                const isSelected = selectedEnvId === id;
                return (
                    <Text strong={isSelected} style={{ color: isSelected ? '#1890ff' : 'inherit' }}>
                        #{id}
                    </Text>
                );
            }
        },
        {
            title: 'Data i Hora',
            dataIndex: 'moment',
            key: 'moment',
            render: (moment) => (
                <Space size={4}>
                    <ClockCircleOutlined style={{ color: '#888' }} />
                    <Text style={{ fontSize: 13 }}>{moment || '-'}</Text>
                </Space>
            )
        },
        {
            title: 'Tipus',
            dataIndex: 'tipus_registre',
            key: 'tipus_registre',
            width: 100,
            render: (tipus, record) => {
                const isDraft = record.draft || record.errors === 'DRAFT_GUARDAT';
                return isDraft ? (
                    <Tag color="orange">GUARDAT</Tag>
                ) : (
                    <Tag color="blue">ENVIAT</Tag>
                );
            }
        },
        {
            title: 'Estat / Resultat',
            dataIndex: 'salida',
            key: 'salida',
            render: (salida, record) => {
                const isDraft = record.draft || record.errors === 'DRAFT_GUARDAT';
                if (isDraft) {
                    return (
                        <Tag color="default" style={{ fontSize: 12 }}>
                            Guardat a BBDD (sense avaluar)
                        </Tag>
                    );
                }

                // Extreure puntuació efectiva si és possible
                let numNota: number | null = (record.nota !== null && record.nota !== undefined) ? Number(record.nota) : null;
                const text = salida || record.errors || '';

                if (numNota === null) {
                    const match = text.match(/(\d+(?:\.\d+)?)\s*\/\s*100/);
                    if (match) {
                        numNota = parseFloat(match[1]);
                    }
                }

                if (numNota !== null) {
                    if (numNota >= 100) {
                        return (
                            <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: 12, padding: '2px 8px' }}>
                                Correcte
                            </Tag>
                        );
                    } else if (numNota > 0) {
                        return (
                            <Tag icon={<ExclamationCircleOutlined />} color="warning" style={{ fontSize: 12, padding: '2px 8px', fontWeight: 600 }}>
                                Parcialment correcte
                            </Tag>
                        );
                    } else {
                        return (
                            <Tag icon={<CloseCircleOutlined />} color="error" style={{ fontSize: 12, padding: '2px 8px' }}>
                                Incorrecte
                            </Tag>
                        );
                    }
                }

                const isParcial = text.toUpperCase().startsWith("PARCIAL") || record.estat === 3;
                if (isParcial) {
                    return (
                        <Tag icon={<ExclamationCircleOutlined />} color="warning" style={{ fontSize: 12, padding: '2px 8px', fontWeight: 600 }}>
                            Parcialment correcte
                        </Tag>
                    );
                }

                const isOk = text.startsWith("OK") || record.estat === 1 || record.estat === 2;
                return (
                    <Tag
                        icon={isOk ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                        color={isOk ? "success" : "error"}
                        style={{ fontSize: 12, padding: '2px 8px' }}
                    >
                        {isOk ? "Correcte" : "Errors"}
                    </Tag>
                );
            }
        },
        {
            title: 'Puntuació',
            dataIndex: 'nota',
            key: 'nota',
            width: 110,
            align: 'center',
            render: (nota, record) => {
                const isDraft = record.draft || record.errors === 'DRAFT_GUARDAT';
                if (isDraft) return <Text type="secondary">-</Text>;

                let num: number | null = (nota !== null && nota !== undefined) ? Number(nota) : null;
                if (num === null) {
                    const text = record.salida || record.errors || '';
                    const match = text.match(/(\d+(?:\.\d+)?)\s*\/\s*100/);
                    if (match) {
                        num = parseFloat(match[1]);
                    }
                }

                if (num === null || isNaN(num)) return <Text type="secondary">-</Text>;

                // Nota base desada; apliquem penalització dinàmica pels intents fallits anteriors a aquesta fila.
                const ppi = getPenalitzacioPerIntent(customConfRef || props.element?.test_config || props.element?.checks);
                const prevFails = ppi > 0 ? enviaments.filter((e: any) => {
                    if (e.draft || e.errors === 'DRAFT_GUARDAT') return false;
                    if (Number(e.id) >= Number(record.id)) return false;
                    const n = (e.nota !== null && e.nota !== undefined) ? Number(e.nota) : null;
                    return n === null || n < 100;
                }).length : 0;
                const { notaFinal } = aplicarPenalitzacio(num, prevFails, ppi);
                const rounded = Math.round(notaFinal);
                return (
                    <Tag color={notaColor(rounded)} style={{ fontWeight: 700, fontSize: 13, padding: '2px 8px', color: '#fff', border: 'none' }} title={prevFails > 0 ? `Nota base ${Math.round(num)} − ${prevFails} intents × ${ppi}` : undefined}>
                        {rounded}/100
                    </Tag>
                );
            }
        },
        ...(isTeacher ? [
            {
                title: 'Accions',
                key: 'accions',
                width: 90,
                align: 'center' as const,
                render: (_: any, record: any) => (
                    <Popconfirm
                        title="Eliminar enviament"
                        description={`Segur que vols eliminar l'enviament #${record.id}? Es recalcularà la nota.`}
                        onConfirm={(e) => {
                            e?.stopPropagation();
                            handleDeleteEnviament(record.id);
                        }}
                        onCancel={(e) => e?.stopPropagation()}
                        okText="Sí, eliminar"
                        cancelText="Cancel·lar"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            size="small"
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={(e) => e.stopPropagation()}
                            title="Eliminar aquest enviament"
                        />
                    </Popconfirm>
                )
            }
        ] : [])
    ];

    // Penalització dinàmica per intents fallits (es recalcula quan canvien els enviaments,
    // p. ex. en eliminar-ne un). La nota BASE ve del validador (designEvalResult.notaBase);
    // la nota final = base − (intents_fallits × penalitzacioPerIntent).
    const penalitzacioPerIntent = getPenalitzacioPerIntent(customConfRef || props.element?.test_config || props.element?.checks);
    // Intents fallits PREVIS a l'enviament seleccionat: enviaments reals (no draft) amb nota < 100
    // que són ANTERIORS (id menor) al seleccionat.
    const intentsFallitsPrevis = (() => {
        if (!selectedEnvId) return 0;
        return enviaments.filter((e: any) => {
            if (e.draft || e.errors === 'DRAFT_GUARDAT') return false;
            if (Number(e.id) >= Number(selectedEnvId)) return false; // només anteriors
            const n = (e.nota !== null && e.nota !== undefined) ? Number(e.nota) : null;
            return n === null || n < 100;
        }).length;
    })();
    // Nota BASE de l'enviament seleccionat: fem servir la nota DESADA del propi enviament
    // (la del registre seleccionat), i si no en té, la recalculada pel validador. NO fem servir
    // MAX de totes les notes: així reflecteix exactament l'enviament que s'està veient.
    const envSeleccionat = enviaments.find((e: any) => e.id === selectedEnvId);
    const notaDesada = (envSeleccionat && envSeleccionat.nota !== null && envSeleccionat.nota !== undefined && !Number.isNaN(Number(envSeleccionat.nota)))
        ? Number(envSeleccionat.nota)
        : null;
    const notaRecalculada = designEvalResult
        ? (designEvalResult.notaBase !== undefined ? designEvalResult.notaBase : designEvalResult.score)
        : null;
    const notaBaseDisseny = notaDesada !== null ? notaDesada : (notaRecalculada !== null ? notaRecalculada : 0);
    const { notaFinal: notaFinalDisseny, penalitzacioTotal: penalitzacioTotalDisseny } =
        aplicarPenalitzacio(notaBaseDisseny, intentsFallitsPrevis, penalitzacioPerIntent);

    const mainContent = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* -------------------- LLISTAT D'ENVIAMENTS -------------------- */}
                    <div>
                        {/* Caixa destacada de l'últim contingut GUARDAT (només per al professor, si és diferent a l'últim enviat) */}
                        {isTeacher && ultimGuardat && (
                            <Alert
                                style={{
                                    marginBottom: 14,
                                    border: selectedEnvId === ultimGuardat.id ? '2px solid #faad14' : '1px solid #ffe58f',
                                    backgroundColor: selectedEnvId === ultimGuardat.id ? '#fffbe6' : '#fafafa',
                                    borderRadius: 8,
                                    cursor: 'pointer'
                                }}
                                onClick={() => selectSubmission(ultimGuardat)}
                                message={
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Space>
                                            <Tag color="warning" style={{ fontWeight: 600 }}>GUARDAT EN BBDD</Tag>
                                            <Text strong>Últim treball guardat per l'alumne (sense avaluar com a enviament)</Text>
                                        </Space>
                                        <Space size="middle">
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                <ClockCircleOutlined style={{ marginRight: 4 }} />
                                                {ultimGuardat.moment || 'Data desconeguda'}
                                            </Text>
                                            <Button
                                                size="small"
                                                type={selectedEnvId === ultimGuardat.id ? "primary" : "default"}
                                                style={{ fontSize: 12 }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    selectSubmission(ultimGuardat);
                                                }}
                                            >
                                                {selectedEnvId === ultimGuardat.id ? "Visualitzant" : "Carregar"}
                                            </Button>
                                        </Space>
                                    </div>
                                }
                                description={
                                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                        L'alumne ha guardat aquesta versió en base de dades. És diferent a l'últim enviament avaluat. Fes clic per inspeccionar-la o re-enviar-la.
                                    </div>
                                }
                                type="warning"
                                showIcon
                            />
                        )}

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <Title level={5} style={{ margin: 0 }}>
                                <HistoryOutlined style={{ marginRight: 6 }} />
                                Historial d'Enviaments Avaluats ({enviaments.length})
                            </Title>
                            {selectedEnvId && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Element visualitzat actualment: <Text strong>#{selectedEnvId}</Text>
                                    {selectedEnvId === ultimGuardat?.id && (
                                        <Tag color="orange" style={{ marginLeft: 8 }}>Últim Guardat (Draft)</Tag>
                                    )}
                                </Text>
                            )}
                        </div>

                        {enviaments.length === 0 && !loading ? (
                            <Alert
                                message="Sense enviaments avaluats"
                                description="L'alumne encara no ha realitzat cap enviament per avaluar en aquest exercici."
                                type="info"
                                showIcon
                            />
                        ) : (
                            <Table
                                size="small"
                                rowKey="id"
                                loading={loading}
                                columns={columns}
                                dataSource={enviaments}
                                pagination={enviaments.length > 5 ? { pageSize: 5, size: 'small' } : false}
                                rowClassName={(record) => record.id === selectedEnvId ? 'ant-table-row-selected' : ''}
                                onRow={(record) => ({
                                    onClick: () => selectSubmission(record),
                                    style: {
                                        cursor: 'pointer',
                                        backgroundColor: record.id === selectedEnvId ? '#e6f7ff' : undefined
                                    }
                                })}
                                style={{
                                    border: '1px solid #e8e8e8',
                                    borderRadius: 6,
                                    overflow: 'hidden'
                                }}
                            />
                        )}
                    </div>

                    <Divider style={{ margin: '4px 0' }} />

                    {/* -------------------- ENUNCIAT EN MARKDOWN -------------------- */}
                    {enunciat && (
                        <div>
                            <Title level={5} style={{ marginBottom: 8 }}>Enunciat de l'Exercici</Title>
                            <div style={{
                                padding: "12px 16px",
                                background: "#fafafa",
                                border: "1px solid #ddd",
                                borderRadius: "6px",
                                maxHeight: "150px",
                                overflowY: "auto"
                            }}>
                                <ReactMarkdown>{enunciat}</ReactMarkdown>
                            </div>
                        </div>
                    )}

                    {/* -------------------- CÒDIG / DIAGRAMA -------------------- */}
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <Title level={5} style={{ margin: 0 }}>
                                {isEr
                                    ? "Diagrama Entitat-Relació (ER)"
                                    : isScript
                                    ? "Script Relacional"
                                    : isRelational
                                    ? "Model Relacional (Taules)"
                                    : isMongo
                                    ? "Consulta NoSQL (MongoDB)"
                                    : "Codi / Consulta de l'Enviament"}
                            </Title>
                            {isTeacher && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Podeu modificar el contingut i fer clic a <Text strong style={{ color: '#52c41a' }}>Re-enviar</Text> per guardar una nova entrega.
                                </Text>
                            )}
                        </div>

                        {isEr ? (
                            <div style={{ height: '420px', border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
                                <ErDiagramIde
                                    initialData={actual}
                                    solutionData={props.element?.correccio}
                                    readOnly={!isTeacher}
                                    hideImportExport={true}
                                    hideSave={true}
                                    hideEvaluate={true}
                                    onSave={(data) => setActual(JSON.stringify(data))}
                                    onManualSave={async (data) => setActual(JSON.stringify(data))}
                                    title={`Model ER (Enviament #${selectedEnvId || ''})`}
                                />
                            </div>
                        ) : isScript ? (
                            <div style={{ height: '380px', border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
                                <ScriptRelationalIde
                                    initialData={actual}
                                    solutionData={props.element?.correccio}
                                    readOnly={!isTeacher}
                                    hideImportExport={true}
                                    hideSave={true}
                                    hideEvaluate={true}
                                    isTeacherMode={isTeacher}
                                    onSave={(data, raw) => setActual(raw)}
                                    onManualSave={async (data, raw) => setActual(raw)}
                                    title={`Script Relacional (Enviament #${selectedEnvId || ''})`}
                                />
                            </div>
                        ) : isRelational ? (
                            <div style={{ height: '420px', border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
                                <RelationalDiagramIde
                                    initialData={actual}
                                    solutionData={props.element?.correccio}
                                    readOnly={!isTeacher}
                                    hideImportExport={true}
                                    hideSave={true}
                                    hideEvaluate={true}
                                    onSave={(data) => setActual(JSON.stringify(data))}
                                    onManualSave={async (data) => setActual(JSON.stringify(data))}
                                    title={`Model Relacional (Enviament #${selectedEnvId || ''})`}
                                />
                            </div>
                        ) : (
                            <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
                                <Editor
                                    height="250px"
                                    defaultLanguage={isMongo ? "javascript" : "sql"}
                                    theme="vs-dark"
                                    value={actual}
                                    onChange={(newVal) => isTeacher && setActual(newVal || "")}
                                    options={{
                                        readOnly: !isTeacher,
                                        minimap: { enabled: false },
                                        fontSize: 14
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    {/* -------------------- RESULTAT / ERRORS -------------------- */}
                    {designEvalResult ? (
                        <div style={{
                            background: '#fafafa',
                            border: '1px solid #e8e8e8',
                            borderRadius: 8,
                            padding: '16px 20px',
                            marginBottom: 8
                        }}>
                            <div style={{ textAlign: 'center', marginBottom: 16 }}>
                                <Title level={4} style={{ margin: 0, color: notaColor(notaFinalDisseny) }}>
                                    {notaFinalDisseny >= 100 ? <CheckCircleOutlined style={{ marginRight: 8 }} /> : (notaFinalDisseny > 0 ? <ExclamationCircleOutlined style={{ marginRight: 8 }} /> : <CloseCircleOutlined style={{ marginRight: 8 }} />)}
                                    Puntuació de l'Enviament: {Math.round(notaFinalDisseny)} / 100
                                </Title>
                                <Text type="secondary" style={{ fontSize: 13 }}>
                                    Puntuació obtinguda en base a la rúbrica i tests de disseny
                                </Text>
                            </div>

                            {/* Mètriques de resum si n'hi ha */}
                            {designEvalResult.summary && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-around',
                                    background: '#ffffff',
                                    padding: '10px 14px',
                                    borderRadius: 6,
                                    border: '1px solid #f0f0f0',
                                    marginBottom: 16,
                                    textAlign: 'center'
                                }}>
                                    {designEvalResult.summary.tablesExpected !== undefined && (
                                        <div>
                                            <div style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 600 }}>Taules</div>
                                            <div style={{ fontSize: 15, fontWeight: 700, color: '#1890ff' }}>
                                                {designEvalResult.summary.tablesMatched} / {designEvalResult.summary.tablesExpected}
                                            </div>
                                        </div>
                                    )}
                                    {designEvalResult.summary.columnsExpected !== undefined && (
                                        <div>
                                            <div style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 600 }}>Columnes</div>
                                            <div style={{ fontSize: 15, fontWeight: 700, color: '#1890ff' }}>
                                                {designEvalResult.summary.columnsMatched} / {designEvalResult.summary.columnsExpected}
                                            </div>
                                        </div>
                                    )}
                                    {designEvalResult.summary.typesMatched !== undefined && (
                                        <div>
                                            <div style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 600 }}>Tipus de dades</div>
                                            <div style={{ fontSize: 15, fontWeight: 700, color: '#52c41a' }}>
                                                {designEvalResult.summary.typesMatched} / {designEvalResult.summary.columnsExpected}
                                            </div>
                                        </div>
                                    )}
                                    {designEvalResult.summary.fksExpected !== undefined && (
                                        <div>
                                            <div style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 600 }}>Relacions (FK)</div>
                                            <div style={{ fontSize: 15, fontWeight: 700, color: '#722ed1' }}>
                                                {designEvalResult.summary.fksMatched} / {designEvalResult.summary.fksExpected}
                                            </div>
                                        </div>
                                    )}
                                    {designEvalResult.summary.entitiesExpected !== undefined && (
                                        <div>
                                            <div style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 600 }}>Entitats</div>
                                            <div style={{ fontSize: 15, fontWeight: 700, color: '#1890ff' }}>
                                                {designEvalResult.summary.entitiesMatched} / {designEvalResult.summary.entitiesExpected}
                                            </div>
                                        </div>
                                    )}
                                    {designEvalResult.summary.relationshipsExpected !== undefined && (
                                        <div>
                                            <div style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 600 }}>Relacions</div>
                                            <div style={{ fontSize: 15, fontWeight: 700, color: '#722ed1' }}>
                                                {designEvalResult.summary.relationshipsMatched} / {designEvalResult.summary.relationshipsExpected}
                                            </div>
                                        </div>
                                    )}
                                    {designEvalResult.summary.attributesExpected !== undefined && (
                                        <div>
                                            <div style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 600 }}>Atributs</div>
                                            <div style={{ fontSize: 15, fontWeight: 700, color: '#13c2c2' }}>
                                                {designEvalResult.summary.attributesMatched} / {designEvalResult.summary.attributesExpected}
                                            </div>
                                        </div>
                                    )}
                                    {designEvalResult.summary.connectionsExpected !== undefined && (
                                        <div>
                                            <div style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 600 }}>Participacions</div>
                                            <div style={{ fontSize: 15, fontWeight: 700, color: '#fa8c16' }}>
                                                {designEvalResult.summary.connectionsMatched} / {designEvalResult.summary.connectionsExpected}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Desglossament de la nota: nota base (desada) i penalització dinàmica per intents */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 24,
                                flexWrap: 'wrap',
                                background: '#ffffff',
                                padding: '8px 14px',
                                borderRadius: 6,
                                border: '1px solid #f0f0f0',
                                marginBottom: 16,
                                fontSize: 13
                            }}>
                                <span>
                                    Nota base (rúbrica): <Text strong style={{ color: '#1890ff' }}>{Math.round(notaBaseDisseny)}/100</Text>
                                </span>
                                {penalitzacioTotalDisseny > 0 && (
                                    <span>
                                        Penalització per intents fallits: <Text strong style={{ color: '#ff4d4f' }}>−{penalitzacioTotalDisseny} pts</Text>
                                        <Text type="secondary"> ({intentsFallitsPrevis} intents × {penalitzacioPerIntent})</Text>
                                    </span>
                                )}
                                <span>
                                    Nota final: <Text strong style={{ color: notaColor(notaFinalDisseny) }}>{Math.round(notaFinalDisseny)}/100</Text>
                                </span>
                            </div>

                            {/* Llista de feedback detallat: què està bé, què avisa i què penalitza */}
                            {Array.isArray(designEvalResult.feedback) && designEvalResult.feedback.length > 0 ? (
                                <div>
                                    {(() => {
                                        const fbs = designEvalResult.feedback as any[];
                                        // Correctes: tot el que és success. A millorar: la resta (errors i avisos).
                                        const correctList = fbs.filter(f => f.diffType === 'correct' || (!f.diffType && f.type === 'success'));
                                        const problemList = fbs.filter(f => !(f.diffType === 'correct' || (!f.diffType && f.type === 'success')));

                                        const categoriaLabel = (cat?: string) => {
                                            switch (cat) {
                                                case 'entity': return 'Entitat';
                                                case 'relationship': return 'Relació';
                                                case 'attribute': return 'Atribut';
                                                case 'connection': return 'Enllaç/Participació';
                                                case 'hierarchy': return 'Jerarquia';
                                                case 'table': return 'Taula';
                                                case 'column': return 'Columna';
                                                case 'pk': return 'Clau primària';
                                                case 'fk': return 'Clau forana';
                                                default: return '—';
                                            }
                                        };

                                        // Taula de CORRECTES (tick verd)
                                        const correctColumns: ColumnsType<any> = [
                                            {
                                                title: '',
                                                key: 'ic',
                                                width: 40,
                                                align: 'center',
                                                render: () => <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
                                            },
                                            {
                                                title: 'Tipus',
                                                dataIndex: 'category',
                                                key: 'cat',
                                                width: 150,
                                                render: (cat) => <Tag color="green">{categoriaLabel(cat)}</Tag>
                                            },
                                            {
                                                title: 'Element correcte',
                                                key: 'msg',
                                                render: (_: any, fb: any) => <span>{fb.itemLabel || fb.message}</span>
                                            },
                                            {
                                                title: 'Punts',
                                                key: 'pts',
                                                width: 90,
                                                align: 'center',
                                                render: (_: any, fb: any) => (typeof fb.pointsEarned === 'number' && fb.pointsEarned > 0)
                                                    ? <Tag color="green">+{fb.pointsEarned}</Tag>
                                                    : <Text type="secondary">—</Text>
                                            }
                                        ];

                                        // Taula d'ERRORS / A MILLORAR (creu vermella o avís)
                                        const problemColumns: ColumnsType<any> = [
                                            {
                                                title: '',
                                                key: 'ic',
                                                width: 40,
                                                align: 'center',
                                                render: (_: any, fb: any) => fb.type === 'warning'
                                                    ? <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 16 }} />
                                                    : <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />
                                            },
                                            {
                                                title: 'Tipus',
                                                dataIndex: 'category',
                                                key: 'cat',
                                                width: 150,
                                                render: (cat) => <Tag color="red">{categoriaLabel(cat)}</Tag>
                                            },
                                            {
                                                title: 'Què falla',
                                                key: 'msg',
                                                render: (_: any, fb: any) => (
                                                    <div>
                                                        <div>{fb.message}</div>
                                                        {(fb.expected || fb.actual) && (
                                                            <div style={{ fontSize: 12, marginTop: 2 }}>
                                                                {fb.expected && <span style={{ color: '#52c41a' }}>Esperat: <Text code>{fb.expected}</Text> </span>}
                                                                {fb.actual && <span style={{ color: '#ff4d4f' }}>Actual: <Text code>{fb.actual}</Text></span>}
                                                            </div>
                                                        )}
                                                    </div>
                                                )
                                            },
                                            {
                                                title: 'Penalització',
                                                key: 'pts',
                                                width: 110,
                                                align: 'center',
                                                render: (_: any, fb: any) => {
                                                    const max = typeof fb.maxPoints === 'number' ? fb.maxPoints : undefined;
                                                    const earned = typeof fb.pointsEarned === 'number' ? fb.pointsEarned : undefined;
                                                    const perdut = (max !== undefined && earned !== undefined) ? Math.max(0, max - earned) : undefined;
                                                    if (perdut !== undefined && perdut > 0) return <Tag color="red">−{perdut} pts</Tag>;
                                                    if (fb.type === 'warning') return <Tag color="orange">avís</Tag>;
                                                    return <Text type="secondary">—</Text>;
                                                }
                                            }
                                        ];

                                        return (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                                {/* Taula de correctes */}
                                                <div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                                        <Text strong style={{ color: '#389e0d' }}>Correctes ({correctList.length})</Text>
                                                    </div>
                                                    {correctList.length === 0 ? (
                                                        <Alert message="Cap element correcte encara." type="warning" showIcon />
                                                    ) : (
                                                        <Table
                                                            size="small"
                                                            rowKey={(_, i) => 'ok_' + i}
                                                            columns={correctColumns}
                                                            dataSource={correctList}
                                                            pagination={false}
                                                            scroll={{ y: 200 }}
                                                            style={{ border: '1px solid #b7eb8f', borderRadius: 6, overflow: 'hidden' }}
                                                        />
                                                    )}
                                                </div>

                                                {/* Taula d'errors / a millorar */}
                                                <div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                                        <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                                                        <Text strong style={{ color: '#cf1322' }}>Errors i penalitzacions ({problemList.length})</Text>
                                                    </div>
                                                    {problemList.length === 0 ? (
                                                        <Alert message="Sense errors! Tot correcte." type="success" showIcon />
                                                    ) : (
                                                        <Table
                                                            size="small"
                                                            rowKey={(_, i) => 'ko_' + i}
                                                            columns={problemColumns}
                                                            dataSource={problemList}
                                                            pagination={false}
                                                            scroll={{ y: 220 }}
                                                            style={{ border: '1px solid #ffa39e', borderRadius: 6, overflow: 'hidden' }}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            ) : (
                                <Alert
                                    type="warning"
                                    showIcon
                                    message="Sense detall de correcció"
                                    description="No hi ha una solució de referència configurada per a aquest exercici, o no s'ha pogut comparar amb la resposta. No es pot mostrar el desglossament de la puntuació."
                                />
                            )}
                        </div>
                    ) : errors && (
                        <Alert
                            message={errors.startsWith("OK") ? "Correcte" : (errors.startsWith("PARCIAL") ? "Parcialment correcte" : "Errors en l'execució")}
                            description={<pre style={{ whiteSpace: "pre-wrap", margin: 0, maxHeight: 120, overflowY: 'auto' }}>{errors}</pre>}
                            type={errors.startsWith("OK") ? "success" : (errors.startsWith("PARCIAL") ? "warning" : "error")}
                            showIcon
                        />
                    )}

                    {/* -------------------- TAULES COMPARATIVES -------------------- */}
                    {(dades?.length > 0 || dadesA?.length > 0) && (
                        <div style={{ display: "flex", gap: "20px", alignItems: "flex-start", marginTop: 4 }}>
                            {/* Resposta correcta */}
                            {dades?.length > 0 && (
                                <div style={{ flex: 1 }}>
                                    <Title level={5} style={{ fontSize: 14 }}>Resultat Esperat</Title>
                                    <div style={{ maxHeight: 200, overflow: 'auto', border: '1px solid #d9d9d9', borderRadius: 4 }}>
                                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                                            <thead>
                                                <tr>
                                                    {Object.keys(dades[0]).map((key) => (
                                                        <th key={key} style={{ padding: "6px 10px", background: "#f0f0f0", borderBottom: "1px solid #d9d9d9", textAlign: 'left' }}>
                                                            {key}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {dades.map((item, index) => (
                                                    <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                                                        {Object.values(item).map((value, idx) => (
                                                            <td key={idx} style={{ padding: "6px 10px" }}>
                                                                {String(value)}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Resposta alumne */}
                            {dadesA?.length > 0 && (
                                <div style={{ flex: 1 }}>
                                    <Title level={5} style={{ fontSize: 14 }}>Resultat Obtingut (Alumne)</Title>
                                    <div style={{ maxHeight: 200, overflow: 'auto', border: '1px solid #d9d9d9', borderRadius: 4 }}>
                                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                                            <thead>
                                                <tr>
                                                    {Object.keys(dadesA[0]).map((key) => (
                                                        <th key={key} style={{ padding: "6px 10px", background: "#f0f0f0", borderBottom: "1px solid #d9d9d9", textAlign: 'left' }}>
                                                            {key}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {dadesA.map((item, index) => (
                                                    <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                                                        {Object.values(item).map((value, idx) => (
                                                            <td key={idx} style={{ padding: "6px 10px" }}>
                                                                {String(value)}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
    );

    if (props.inline) {
        return (
            <ConfigProvider locale={locale}>
                {mainContent}
            </ConfigProvider>
        );
    }

    return (
        <ConfigProvider locale={locale}>
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 32 }}>
                        <Space>
                            <HistoryOutlined style={{ color: '#1890ff', fontSize: 20 }} />
                            <span style={{ fontSize: 16, fontWeight: 700 }}>
                                Historial i Visualització d'Enviaments · {props.element?.prac || props.element?.nom || `Exercici ${props.element?.id}`}
                            </span>
                        </Space>
                        {isTeacher ? (
                            <Tag color="gold" style={{ fontWeight: 600 }}>Mode Professor (Edició i Re-enviament)</Tag>
                        ) : (
                            <Tag color="blue" style={{ fontWeight: 600 }}>Mode Alumne (Només Lectura)</Tag>
                        )}
                    </div>
                }
                open={props.visible}
                onCancel={handleCancel}
                footer={[
                    <Button key="close" onClick={handleCancel}>
                        Tancar
                    </Button>,
                    ...(isTeacher ? [
                        <Button
                            key="resend"
                            type="primary"
                            icon={<SendOutlined />}
                            loading={submitting}
                            onClick={handleReEnviar}
                            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                        >
                            Re-enviar
                        </Button>
                    ] : [])
                ]}
                width={1100}
                style={{ top: 20 }}
            >
                {mainContent}
            </Modal>
        </ConfigProvider>
    );
};

export default RevisioModal;
